import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/lib/toast";
import { Eye, EyeOff, AlertTriangle, Clock, HelpCircle } from "lucide-react";

export default function PortalLoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { portalLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await portalLogin(username, password);
      if (result.must_change_password) {
        toast.info("Pour votre securite, veuillez choisir un nouveau mot de passe.");
        navigate("/portal/change-password");
      } else {
        toast.success("Bienvenue sur votre portail");
        navigate("/portal/dashboard");
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Erreur de connexion";

      if (status === 429) {
        setError({ type: "lockout", message: msg });
      } else if (status === 401) {
        setError({ type: "credentials", message: "Nom d'utilisateur ou mot de passe incorrect." });
      } else if (status === 400) {
        setError({ type: "validation", message: msg });
      } else {
        setError({ type: "generic", message: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-[#072354] to-[#2877D1]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="portal-login-card">
        <div className="px-6 py-7 text-white text-center bg-gradient-to-r from-[#072354] to-[#2877D1]">
          <div className="flex items-center justify-center gap-3 mb-1">
            <img src="/dg-logo.png" alt="DG Fiscal Flow" className="w-12 h-12 object-contain" />
            <div className="text-left">
              <h1 className="text-xl font-bold">DG Fiscal Flow</h1>
              <p className="text-xs text-blue-200">Espace client</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="portal-login-form">
          <h2 className="text-base font-semibold text-gray-700 text-center">Connexion a votre dossier</h2>

          {error && (
            <div className={`rounded-lg p-3 flex items-start gap-2 ${
              error.type === "lockout" ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"
            }`} data-testid="portal-login-error">
              {error.type === "lockout" ? (
                <Clock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              )}
              <div>
                <p className={`text-sm ${error.type === "lockout" ? "text-amber-700" : "text-red-700"}`}>{error.message}</p>
                {error.type === "lockout" && (
                  <p className="text-xs text-amber-600 mt-1">Votre acces sera retabli automatiquement. Si le probleme persiste, contactez votre équipe.</p>
                )}
                {error.type === "credentials" && (
                  <p className="text-xs text-red-500 mt-1">Vous pouvez réinitialiser votre mot de passe avec le lien « Mot de passe oublié ? » ci-dessous.</p>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'utilisateur ou courriel</label>
            <input type="text" value={username} onChange={(e) => { setUsername(e.target.value); setError(null); }}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#072354] focus:ring-2 focus:ring-[#072354]/20 text-sm"
              placeholder="Nom d'utilisateur ou courriel" data-testid="portal-username-input" autoFocus />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => { setPassword(e.target.value); setError(null); }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-transparent focus:ring-2 pr-10 text-sm"
                placeholder="Votre mot de passe" data-testid="portal-password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading || !username || !password}
            className="w-full text-white py-3 rounded-lg transition font-medium disabled:opacity-50 text-sm bg-[#072354] hover:bg-[#0a2d6e]"
            data-testid="portal-login-btn">
            {loading ? "Connexion..." : "Se connecter"}
          </button>

          <div className="flex justify-center">
            <Link to="/portal/forgot-password" className="text-sm text-[#2877D1] hover:underline" data-testid="portal-forgot-password-link">
              Mot de passe oublié ?
            </Link>
          </div>

          <div className="flex items-center justify-center gap-1 text-xs text-gray-400 pt-2 border-t">
            <HelpCircle className="w-3 h-3" />
            <span>Probleme de connexion? Contactez votre équipe.</span>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="portal-demo-credentials">
            <p className="text-sm font-semibold text-gray-700 mb-2">Portail démo :</p>
            <div className="space-y-1.5 text-sm text-gray-600">
              <p>
                Utilisateur :{" "}
                <button type="button" onClick={() => { setUsername("demo-portail"); setError(null); }}
                  className="font-mono bg-white border border-gray-300 rounded px-2 py-0.5 hover:border-[#2877D1] transition"
                  data-testid="demo-username-chip">
                  demo-portail
                </button>
              </p>
              <p>
                Mot de passe :{" "}
                <button type="button" onClick={() => { setPassword("demo123"); setError(null); }}
                  className="font-mono bg-white border border-gray-300 rounded px-2 py-0.5 hover:border-[#2877D1] transition"
                  data-testid="demo-password-chip">
                  demo123
                </button>
              </p>
            </div>
            <p className="text-xs text-gray-400 italic mt-2">
              Explorez le portail avec des données fictives — cliquez sur les identifiants pour remplir les champs.
            </p>
          </div>
          <div className="flex justify-center pt-1">
            <Link to="/" className="text-sm text-gray-500 hover:underline">Page d'accueil</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
